<template>
  <Teleport :to="'body'">
    <Modal
      ref="mergeAnnotationModal"
      v-model:show="isShown"
      class="merge-annotation-dialog"
      :width="'30%'"
      :title="'Merge Annotations'"
    >
      <template #modal-main>
        <div class="body">
          <template v-if="status === 'idle'">
            <div class="form__control-group">
              <label for="merge-annotations-iou">IOU (0 - 1)</label>
              <input
                id="merge-annotations-iou"
                v-model.number="iou"
                type="number"
                class="select"
                min="0"
                max="1"
                step="0.01"
              >
            </div>
            <div class="form__control-group">
              <label for="merge-annotations-set">Annotation Set</label>
              <select
                id="merge-annotations-set"
                v-model="selectedAnnotationSetId"
                class="select"
              >
                <option :value="null" disabled>Select Annotation Set</option>
                <option
                  v-for="set in annotationSets"
                  :key="set.id"
                  :value="set.id"
                >
                  {{ set.name }}
                </option>
              </select>
            </div>
            <p v-if="validationError" class="merge-annotation-dialog__error">{{ validationError }}</p>
          </template>

          <div v-else-if="status === 'loading'" class="merge-annotation-dialog__loading">
            <InlineLoader :width="'50px'" :height="'50px'" :border="'8px'" />
            <p>Merging annotations…</p>
          </div>

          <div v-else-if="status === 'error'" class="merge-annotation-dialog__error-state">
            <p class="merge-annotation-dialog__error">{{ errorMessage }}</p>
          </div>

          <div v-else-if="status === 'success'" class="merge-annotation-dialog__result">
            <div class="merge-annotation-dialog__result-row">
              <span class="label">Annotation Set</span>
              <span class="value">{{ result.annotation_set_id }}</span>
            </div>
            <div class="merge-annotation-dialog__result-row">
              <span class="label">Segmentation Processed</span>
              <span class="value">{{ result.processed_seg }}</span>
            </div>
            <div class="merge-annotation-dialog__result-row">
              <span class="label">Segmentation Merged</span>
              <span class="value">{{ result.merged }}</span>
            </div>
            <div class="merge-annotation-dialog__result-row">
              <span class="label">Segmentation Skipped</span>
              <span class="value">{{ result.skipped }}</span>
            </div>
            <div class="merge-annotation-dialog__result-row">
              <span class="label">3D Processed</span>
              <span class="value">{{ result.processed_3d }}</span>
            </div>
            <div class="merge-annotation-dialog__result-row">
              <span class="label">3D Merged</span>
              <span class="value">{{ result.merged_3d }}</span>
            </div>
            <div class="merge-annotation-dialog__result-row">
              <span class="label">3D Skipped</span>
              <span class="value">{{ result.skipped_3d }}</span>
            </div>
          </div>
        </div>
      </template>
      <template #modal-footer>
        <div class="footer">
          <template v-if="status === 'idle'">
            <button class="dialog-button dialog-button-cancel" @click="closeModal">Cancel</button>
            <button class="dialog-button" @click="handleMergeClicked">Merge</button>
          </template>
          <template v-else-if="status === 'error'">
            <button class="dialog-button dialog-button-cancel" @click="closeModal">Close</button>
            <button class="dialog-button" @click="resetToForm">Try Again</button>
          </template>
          <template v-else-if="status === 'success'">
            <button class="dialog-button" @click="closeModal">Close</button>
          </template>
        </div>
      </template>
    </Modal>
  </Teleport>
</template>

<script>
import Modal from '@/components/Modal.vue';
import InlineLoader from '@/components/InlineLoader.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

export default {
  name: 'MergeAnnotationDialog',
  components: {
    Modal,
    InlineLoader,
  },
  props: {
    datasetId: {
      type: [Number, String],
      default: null,
    },
    annotationSets: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isShown: false,
      status: 'idle',
      iou: 0.5,
      selectedAnnotationSetId: null,
      validationError: '',
      errorMessage: '',
      result: null,
      dataConnect: new DatastoreConnect(),
    };
  },
  methods: {
    showModal() {
      this.resetToForm();
      this.$refs.mergeAnnotationModal.showModal();
    },
    closeModal() {
      this.$refs.mergeAnnotationModal.closeModal();
    },
    resetToForm() {
      this.status = 'idle';
      this.validationError = '';
      this.errorMessage = '';
      this.result = null;
    },
    async handleMergeClicked() {
      this.validationError = '';

      if (this.iou === null || this.iou === '' || Number.isNaN(this.iou) || this.iou < 0 || this.iou > 1) {
        this.validationError = 'IOU must be a number between 0 and 1.';
        return;
      }
      if (!this.selectedAnnotationSetId) {
        this.validationError = 'Please select an annotation set.';
        return;
      }

      this.status = 'loading';

      await this.dataConnect.api('dataset.merge.annotations', {
        dataset_id: this.datasetId,
        iou: this.iou,
        annotation_set_id: this.selectedAnnotationSetId,
      })
        .then((resp) => {
          if (resp.error) {
            throw new Error(resp.error.message || resp.error);
          }
          this.result = resp.result;
          this.status = 'success';
        })
        .catch((error) => {
          this.errorMessage = error?.message || String(error);
          this.status = 'error';
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';

.body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0.75rem 2rem;
}

.footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0.75rem 2rem;
}

.merge-annotation-dialog {
  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 24px 0;
  }

  &__error {
    color: var(--color-error, #b00020);
    font-size: 0.85rem;
  }

  &__error-state {
    padding: 12px 0;
  }

  &__result {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__result-row {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);

    .label {
      font-weight: 600;
    }
  }
}
</style>
